import { useState } from 'react';
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { Check, Fingerprint, ShieldCheck, Smartphone, Trash2 } from 'lucide-react';
import type { TotpSetupResponse } from '@proerp/shared';
import {
  Button,
  Dialog,
  DialogContent,
  EmptyState,
  FormField,
  Input,
  Skeleton,
} from '@proerp/ui';
import { QrCanvas } from '@/components/QrCanvas';
import { formatDateTime } from '@/lib/format';
import { guessDeviceLabel } from '@/lib/auth';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useToasts } from '@/lib/toast';
import {
  useDeletePasskey,
  usePasskeys,
  useRegisterPasskey,
  useRegisterPasskeyOptions,
} from '@/features/account/passkeyApi';
import { useConfirmTotp, useEnrollTotpOptions, useRemoveTotp } from '@/features/account/totpApi';

/**
 * Self-Service-Verwaltung der zweiten Faktoren (ADR 0034): Authenticator (TOTP)
 * UND Passkeys — gleichrangig, jederzeit hinzufügbar/entfernbar. Der letzte
 * verbleibende Faktor kann nicht entfernt werden (Backend erzwingt das).
 */
export function SecuritySettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const push = useToasts((state) => state.push);
  const { data: me } = useCurrentUser();
  const isTotpEnabled = me?.data.isTotpEnabled ?? false;

  const { data, isLoading } = usePasskeys();
  const registerOptions = useRegisterPasskeyOptions();
  const registerPasskey = useRegisterPasskey();
  const deletePasskey = useDeletePasskey();

  const enrollTotpOptions = useEnrollTotpOptions();
  const confirmTotp = useConfirmTotp();
  const removeTotp = useRemoveTotp();

  const [isAdding, setIsAdding] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState('');
  // TOTP-Einrichtung: sobald ein Geheimnis geholt wurde, QR + Code-Eingabe zeigen.
  const [totpSetup, setTotpSetup] = useState<TotpSetupResponse | null>(null);
  const [totpCode, setTotpCode] = useState('');

  const passkeys = data?.data ?? [];
  const isBusy = registerOptions.isPending || registerPasskey.isPending;

  function resetTransient() {
    setIsAdding(false);
    setDeviceLabel('');
    setTotpSetup(null);
    setTotpCode('');
  }

  async function startTotpSetup() {
    try {
      const response = await enrollTotpOptions.mutateAsync();
      setTotpSetup(response.data);
      setTotpCode('');
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Einrichtung konnte nicht gestartet werden.');
    }
  }

  async function confirmTotpSetup() {
    try {
      await confirmTotp.mutateAsync(totpCode.trim());
      push('success', 'Authenticator eingerichtet.');
      setTotpSetup(null);
      setTotpCode('');
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Code ungültig — bitte erneut versuchen.');
    }
  }

  async function removeTotpFactor() {
    if (!window.confirm('Authenticator (TOTP) wirklich entfernen?')) return;
    try {
      await removeTotp.mutateAsync();
      push('success', 'Authenticator entfernt.');
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Entfernen fehlgeschlagen.');
    }
  }

  async function confirmAddPasskey() {
    try {
      const options = await registerOptions.mutateAsync();
      const attestationResponse = await startRegistration({
        optionsJSON: options.data.options as PublicKeyCredentialCreationOptionsJSON,
      });
      await registerPasskey.mutateAsync({
        challengeToken: options.data.challengeToken,
        attestationResponse,
        deviceLabel: deviceLabel.trim() || 'Mein Gerät',
      });
      push('success', 'Passkey hinzugefügt.');
      setIsAdding(false);
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Passkey konnte nicht hinzugefügt werden.');
    }
  }

  async function removePasskey(id: string, label: string) {
    if (!window.confirm(`Passkey „${label}" wirklich entfernen?`)) return;
    try {
      await deletePasskey.mutateAsync(id);
      push('success', 'Passkey entfernt.');
    } catch (error) {
      push('danger', error instanceof Error ? error.message : 'Entfernen fehlgeschlagen.');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) resetTransient();
        onOpenChange(value);
      }}
    >
      <DialogContent
        title="Sicherheit"
        footer={
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        }
      >
        <div className="flex flex-col gap-6">
          {/* ── Authenticator (TOTP) ── */}
          <div className="flex flex-col gap-3">
            <div>
              <h3 className="text-sm font-medium text-foreground">Authenticator-App (TOTP)</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Zeitbasierter 6-stelliger Code aus Google/Microsoft Authenticator, Authy … — als zweiter Faktor, auch neben einem Passkey.
              </p>
            </div>

            {isTotpEnabled ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className="h-4 w-4 text-success-500" strokeWidth={1.5} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">Authenticator aktiv</span>
                    <span className="text-xs text-muted-foreground">Beim Login wird ein 6-stelliger Code abgefragt.</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Authenticator entfernen"
                  isLoading={removeTotp.isPending}
                  onClick={() => void removeTotpFactor()}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            ) : totpSetup ? (
              <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">
                  Scanne den QR-Code mit deiner Authenticator-App und gib den angezeigten 6-stelligen Code ein.
                </p>
                <div className="flex justify-center">
                  <QrCanvas value={totpSetup.otpauthUrl} size={180} />
                </div>
                <div className="text-center">
                  <span className="text-xs text-muted-foreground">Falls der Scan nicht geht, Schlüssel manuell eingeben:</span>
                  <div className="mt-1 break-all rounded bg-muted px-2 py-1 font-mono text-xs text-foreground">
                    {totpSetup.secret}
                  </div>
                </div>
                <FormField label="6-stelliger Code" htmlFor="totpCode">
                  <Input
                    id="totpCode"
                    value={totpCode}
                    onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                  />
                </FormField>
                <div className="flex gap-2">
                  <Button variant="ghost" className="flex-1 justify-center" onClick={() => { setTotpSetup(null); setTotpCode(''); }}>
                    Abbrechen
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1 justify-center"
                    isLoading={confirmTotp.isPending}
                    disabled={totpCode.length !== 6}
                    onClick={() => void confirmTotpSetup()}
                  >
                    <Check className="h-4 w-4" strokeWidth={1.5} />
                    Aktivieren
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="secondary"
                className="w-full justify-center"
                isLoading={enrollTotpOptions.isPending}
                onClick={() => void startTotpSetup()}
              >
                <Smartphone className="h-4 w-4" strokeWidth={1.5} />
                Authenticator einrichten
              </Button>
            )}
          </div>

          {/* ── Passkeys ── */}
          <div className="flex flex-col gap-3">
            <div>
              <h3 className="text-sm font-medium text-foreground">Passkeys</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Anmeldung per Face ID, Touch ID oder Geräte-PIN — als Alternative oder Ergänzung zum Authenticator-Code.
              </p>
            </div>

            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : passkeys.length === 0 && !isAdding ? (
              <EmptyState
                icon={Fingerprint}
                title="Noch kein Passkey"
                description="Füge einen Passkey hinzu, um dich ohne Authenticator-Code anzumelden."
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {passkeys.map((passkey) => (
                  <li
                    key={passkey.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
                  >
                    <div className="flex items-center gap-2.5">
                      <Fingerprint className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">{passkey.deviceLabel}</span>
                        <span className="text-xs text-muted-foreground">
                          Hinzugefügt {formatDateTime(passkey.createdAt)}
                          {passkey.lastUsedAt ? ` · zuletzt genutzt ${formatDateTime(passkey.lastUsedAt)}` : ' · noch nicht genutzt'}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Passkey entfernen"
                      isLoading={deletePasskey.isPending}
                      onClick={() => removePasskey(passkey.id, passkey.deviceLabel)}
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {isAdding ? (
              <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
                <FormField label="Gerätename" htmlFor="passkeyLabel">
                  <Input
                    id="passkeyLabel"
                    value={deviceLabel}
                    onChange={(event) => setDeviceLabel(event.target.value)}
                    placeholder={guessDeviceLabel()}
                  />
                </FormField>
                <div className="flex gap-2">
                  <Button variant="ghost" className="flex-1 justify-center" onClick={() => setIsAdding(false)}>
                    Abbrechen
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1 justify-center"
                    isLoading={isBusy}
                    onClick={() => void confirmAddPasskey()}
                  >
                    <Fingerprint className="h-4 w-4" strokeWidth={1.5} />
                    Bestätigen
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="secondary"
                className="w-full justify-center"
                onClick={() => {
                  setDeviceLabel(guessDeviceLabel());
                  setIsAdding(true);
                }}
              >
                <Fingerprint className="h-4 w-4" strokeWidth={1.5} />
                Passkey hinzufügen
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
